import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';

// v10 keys by active home unit id only. Account switches clear the cache in
// clearDataOnLogin; home switches clear it in switchUnit.
const CACHE_VERSION = 10;
const CACHE_PREFIX = `shelly:energy-heatmap:v${CACHE_VERSION}`;
const MAX_CACHE_ENTRIES = 12;

type CachedHeatmapHistory = {
	updatedAt: number;
	coverageFrom?: number;
	coverageTo?: number;
	history: GetStatsResponseHistoryData[];
};

const storage = (): Storage | null => {
	try {
		return typeof window === 'undefined' ? null : window.localStorage;
	} catch {
		return null;
	}
};

const safeKeyPart = (value: unknown): string =>
	encodeURIComponent(
		String(value ?? 'anonymous')
			.trim()
			.toLowerCase(),
	);

/**
 * @param unitId - Active home (`currentUnitId`). Isolates heatmap history per home.
 */
export const buildEnergyHeatmapCacheKey = (
	unitId: unknown,
	scope: string,
	range: 'year' | 'month',
	bucket = '',
): string =>
	`${CACHE_PREFIX}:${safeKeyPart(unitId)}:${safeKeyPart(scope)}:${range}:${safeKeyPart(bucket)}`;

const readEnergyHeatmapCacheEntry = (key: string): CachedHeatmapHistory | null => {
	const target = storage();
	if (!target) return null;
	try {
		const parsed = JSON.parse(target.getItem(key) ?? '') as CachedHeatmapHistory;
		return Array.isArray(parsed?.history) ? parsed : null;
	} catch {
		return null;
	}
};

export const readEnergyHeatmapCache = (key: string): GetStatsResponseHistoryData[] =>
	readEnergyHeatmapCacheEntry(key)?.history ?? [];

export const energyHeatmapCacheCoversRange = (
	key: string,
	controls: ChartControlsDataInterface,
): boolean => {
	const entry = readEnergyHeatmapCacheEntry(key);
	if (!entry || !Number.isFinite(entry.coverageFrom) || !Number.isFinite(entry.coverageTo)) {
		return false;
	}
	const startOfToday = new Date(controls.time.to);
	startOfToday.setHours(0, 0, 0, 0);
	return (
		Number(entry.coverageFrom) <= controls.time.from.getTime() &&
		Number(entry.coverageTo) >= startOfToday.getTime()
	);
};

const pruneHeatmapCache = (target: Storage, keepKey: string) => {
	const entries: Array<{ key: string; updatedAt: number }> = [];
	for (let index = 0; index < target.length; index += 1) {
		const key = target.key(index);
		if (!key?.startsWith(`${CACHE_PREFIX}:`) || key === keepKey) continue;
		try {
			const value = JSON.parse(target.getItem(key) ?? '') as CachedHeatmapHistory;
			entries.push({ key, updatedAt: Number(value?.updatedAt) || 0 });
		} catch {
			entries.push({ key, updatedAt: 0 });
		}
	}
	entries
		.sort((a, b) => b.updatedAt - a.updatedAt)
		.slice(Math.max(MAX_CACHE_ENTRIES - 1, 0))
		.forEach((entry) => target.removeItem(entry.key));
};

export const mergeEnergyHeatmapHistory = (
	cached: GetStatsResponseHistoryData[],
	fresh: GetStatsResponseHistoryData[],
): GetStatsResponseHistoryData[] => {
	const points = new Map<string, GetStatsResponseHistoryData>();
	for (const point of [...cached, ...fresh]) {
		if (point?.datetime) {
			const identity = [point.datetime, point.tariff_id ?? '', point.purpose ?? ''].join('|');
			points.set(identity, point);
		}
	}
	return [...points.values()].sort((a, b) => a.datetime.localeCompare(b.datetime));
};

export const writeEnergyHeatmapCache = (
	key: string,
	history: GetStatsResponseHistoryData[],
	coverage?: ChartControlsDataInterface,
): void => {
	const target = storage();
	if (!target) return;
	try {
		pruneHeatmapCache(target, key);
		target.setItem(
			key,
			JSON.stringify({
				updatedAt: Date.now(),
				coverageFrom: coverage?.time.from.getTime(),
				coverageTo: coverage?.time.to.getTime(),
				history,
			}),
		);
	} catch {
		// Storage can be disabled or full. The network result remains the source of truth.
	}
};

/** Drops all heatmap localStorage entries for the current cache version. */
export const clearEnergyHeatmapCache = (): void => {
	const target = storage();
	if (!target) return;
	const keysToRemove: string[] = [];
	for (let index = 0; index < target.length; index += 1) {
		const key = target.key(index);
		if (key?.startsWith(`${CACHE_PREFIX}:`)) keysToRemove.push(key);
	}
	keysToRemove.forEach((key) => target.removeItem(key));
};

export const buildHeatmapRefreshControls = (
	controls: ChartControlsDataInterface,
	hasCachedHistory: boolean,
): ChartControlsDataInterface => {
	if (!hasCachedHistory) return controls;
	const today = new Date(controls.time.to);
	today.setHours(0, 0, 0, 0);
	return {
		...controls,
		time: {
			from: new Date(Math.max(controls.time.from.getTime(), today.getTime())),
			to: controls.time.to,
		},
	};
};
